import { useEffect, useMemo, useState } from 'react';

import AIJobProgressLoader, { FileAnalysisSubtitle } from 'components/ai/AIJobProgressLoader';
import SlideoutLoaderContainer from 'components/shared/ai/SlideoutLoaderContainer';
import JobbableAttachmentJobSubscriber from 'components/shared/ai/summary/JobbableAttachmentJobSubscriber';
import TimingExplainer from 'components/shared/ai/TimingExplainer';
import { useStageTimer } from 'hooks/useStageTimer';
import { SummarizableReview } from 'lib/aiJobHelpers';

type SummaryLoadingStateProps = {
  review: SummarizableReview;
  onLoadingFinished: () => void;
};

export default function SummaryLoadingState({ review, onLoadingFinished }: SummaryLoadingStateProps) {
  const { attachments, summaryJob } = review;

  const fileCount = attachments.length;
  const summaryJobUpdatedAt = summaryJob?.updatedAt;

  // Only start the timer when the job is awaiting (file processing is done)
  const timerReferenceDate = summaryJob?.isAwaiting ? summaryJobUpdatedAt : null;
  const onFinalStage = useStageTimer(timerReferenceDate, 60);

  const stage = useMemo(() => {
    if (summaryJob?.isCompleted) {
      return 4;
    }
    if (summaryJob?.isAwaiting) {
      if (onFinalStage) {
        return 3;
      } else {
        return 2;
      }
    }
    if (summaryJob?.isPending) {
      return 1;
    }
    return 0;
  }, [summaryJob, onFinalStage]);

  const count = useMemo(() => {
    return stage
      ? attachments.filter(
          (attachment) => attachment.processDocumentAIJob && !attachment.processDocumentAIJob.isProcessing
        ).length // completed or errored
      : 0;
  }, [stage, attachments]);

  const [completedCount, setCompletedCount] = useState(count);

  useEffect(() => {
    if (stage) {
      const timer = setTimeout(() => {
        setCompletedCount(count);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [stage, count]);

  const stages = useMemo(
    () => [
      {
        title: 'Processing files',
        subtitle: `${completedCount} of ${fileCount} complete`,
        subtasks: [
          { title: 'Scanning document text', annotation: `${completedCount}/${fileCount}` },
          { title: 'Extracting key information', annotation: `${completedCount}/${fileCount}` },
          { title: 'Creating source citations', annotation: `${completedCount}/${fileCount}` },
        ],
      },
      { title: 'Generating summary' },
      { title: 'Finishing up' },
    ],
    [completedCount, fileCount]
  );

  return (
    <SlideoutLoaderContainer>
      {attachments.map((attachment) => (
        <JobbableAttachmentJobSubscriber key={attachment.id} attachment={attachment} jobbable={review} />
      ))}
      <AIJobProgressLoader
        title='Generating Summary'
        subtitle={<FileAnalysisSubtitle fileCount={fileCount} />}
        stages={stages}
        stage={stage}
        onLoadingFinished={onLoadingFinished}
      />
      <TimingExplainer jobType='summary' />
    </SlideoutLoaderContainer>
  );
}
